import React from 'react';

const SITE_URL = 'https://xn--giedabudowlana-inc.pl';
const SITE_NAME = 'Giełda Budowlana';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * SEO Component - Manages all meta tags, Open Graph, Twitter Cards, and JSON-LD
 * 
 * @param {string} title - Page title
 * @param {string} description - Page description (max 160 chars)
 * @param {string} canonical - Canonical URL path (e.g., '/offers')
 * @param {string} image - OG image URL
 * @param {string} type - OG type (website, article, profile)
 * @param {object} structuredData - JSON-LD structured data object
 * @param {boolean} noindex - Whether to noindex the page
 */
const upsert = (selector, tag, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

/**
 * SEO - ustawia tytuł, meta tagi i JSON-LD (React 19: bez react-helmet-async, ktory tu nie dziala)
 */
const SEO = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = 'website',
  structuredData = null,
  noindex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Ogłoszenia budowlane i remontowe`;
  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const metaDescription = description || 'Znajdź sprawdzonych wykonawców budowlanych w całej Polsce. Publikuj zlecenia remontowe i budowlane za darmo. Porównaj oferty i opinie firm.';

  React.useEffect(() => {
    document.title = fullTitle;

    upsert('meta[name="description"]', 'meta', { name: 'description', content: metaDescription });
    upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: fullCanonical });
    upsert('meta[name="robots"]', 'meta', {
      name: 'robots',
      content: noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    });

    const og = {
      'og:type': type,
      'og:url': fullCanonical,
      'og:title': fullTitle,
      'og:description': metaDescription,
      'og:image': image,
      'og:image:width': '1200',
      'og:image:height': '630',
      'og:site_name': SITE_NAME,
      'og:locale': 'pl_PL',
    };
    Object.entries(og).forEach(([property, content]) => {
      upsert(`meta[property="${property}"]`, 'meta', { property, content });
    });

    const twitter = {
      'twitter:card': 'summary_large_image',
      'twitter:url': fullCanonical,
      'twitter:title': fullTitle,
      'twitter:description': metaDescription,
      'twitter:image': image,
    };
    Object.entries(twitter).forEach(([name, content]) => {
      upsert(`meta[name="${name}"]`, 'meta', { name, content });
    });
  }, [fullTitle, metaDescription, fullCanonical, image, type, noindex]);

  React.useEffect(() => {
    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    };
  }, [structuredData]);

  return null;
};

/**
 * Generate Organization Schema
 */
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Giełda Budowlana",
  "url": SITE_URL,
  "logo": `${SITE_URL}/logo512.png`,
  "description": "Ogólnopolski portal z ogłoszeniami budowlanymi i remontowymi. Znajdź wykonawców i publikuj zlecenia.",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "PL",
    "addressLocality": "Warszawa"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "kontakt@xn--giedabudowlana-inc.pl",
    "availableLanguage": "Polish"
  },
  "sameAs": []
});

/**
 * Generate WebSite Schema with SearchAction
 */
export const getWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Giełda Budowlana",
  "url": SITE_URL,
  "description": "Portal z ogłoszeniami budowlanymi i remontowymi",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/offers?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

/**
 * Generate LocalBusiness Schema for a company
 */
export const getLocalBusinessSchema = (company) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/companies/${company.user_id}`,
  "name": company.company_name || company.name,
  "description": company.description,
  "url": `${SITE_URL}/companies/${company.user_id}`,
  "image": company.logo_url,
  "address": company.address ? {
    "@type": "PostalAddress",
    "streetAddress": company.address,
    "addressLocality": company.city,
    "addressCountry": "PL"
  } : undefined,
  "telephone": company.phone,
  "email": company.contact_email || company.email,
  "priceRange": "$$",
  "aggregateRating": company.rating ? {
    "@type": "AggregateRating",
    "ratingValue": company.rating,
    "reviewCount": company.review_count || 1,
    "bestRating": "5",
    "worstRating": "1"
  } : undefined,
  "areaServed": {
    "@type": "Country",
    "name": "Polska"
  }
});

/**
 * Generate JobPosting Schema for an offer/listing
 */
export const getJobPostingSchema = (offer) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  "title": offer.title,
  "description": offer.description,
  "datePosted": offer.created_at,
  "validThrough": offer.deadline || new Date(new Date(offer.created_at).getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(),
  "employmentType": "CONTRACT",
  "hiringOrganization": {
    "@type": "Organization",
    "name": "Giełda Budowlana",
    "sameAs": SITE_URL
  },
  "jobLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": offer.location,
      "addressCountry": "PL"
    }
  },
  "industry": "Budownictwo",
  "occupationalCategory": getCategoryName(offer.category)
});

/**
 * Generate Service Schema for construction services
 */
export const getServiceSchema = (category) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": category.name,
  "provider": {
    "@type": "Organization",
    "name": "Giełda Budowlana"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Polska"
  },
  "description": `Znajdź wykonawców w kategorii: ${category.name}. Porównaj oferty i opinie firm budowlanych.`
});

/**
 * Generate BreadcrumbList Schema
 */
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${SITE_URL}${item.path}`
  }))
});

/**
 * Generate FAQPage Schema
 */
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Helper function for category names
const getCategoryName = (categoryId) => {
  const categories = {
    remont: "Remonty ogólne",
    elektryka: "Elektryka",
    hydraulika: "Hydraulika",
    malowanie: "Malowanie i tapetowanie",
    podlogi: "Podłogi i posadzki",
    dachy: "Dachy i pokrycia",
    okna: "Okna i drzwi",
    ogrzewanie: "Ogrzewanie i CO",
    wentylacja: "Wentylacja i klimatyzacja",
    meble: "Meble",
    inne: "Inne prace"
  };
  return categories[categoryId] || categoryId;
};

export default SEO;
